import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AuthenticationResponseDto,
  ContractorAuthenticationResponseDto,
  ContractorRegisterRequestDto,
  FacilityAdminAuthenticationResponseDto,
  FacilityAdminRegisterRequestDto,
  LoginRequestDto,
  RegisterRequestDto,
  ResetPasswordRequestDto,
  SocialLoginRequestDto,
  UserDto,
  CorporateAdminRegisterRequestDto,
  CorporateAdminAuthenticationResponseDto
} from '../../../models/auth.model';

@Injectable({ providedIn: 'root' })
export class AuthAPIService {
  private http = inject(HttpClient);
  private apiUrl = '/api/auth';

  /** Calls /login endpoint. */
  login(req: LoginRequestDto): Observable<AuthenticationResponseDto> {
    return this.http.post<AuthenticationResponseDto>(
      `${this.apiUrl}/login`,
      req,
      { withCredentials: true }
    );
  }

  registerContractor(
    req: ContractorRegisterRequestDto
  ): Observable<ContractorAuthenticationResponseDto> {
    return this.http.post<ContractorAuthenticationResponseDto>(
      `${this.apiUrl}/register/contractor`,
      req
    );
  }

  registerCorporateAdmin(
    req: CorporateAdminRegisterRequestDto
  ): Observable<CorporateAdminAuthenticationResponseDto> {
    return this.http.post<CorporateAdminAuthenticationResponseDto>(
      `${this.apiUrl}/register/corporate-admin`,
      req
    );
  }

  /** Calls /register endpoint. */
  register(req: RegisterRequestDto): Observable<AuthenticationResponseDto> {
    return this.http.post<AuthenticationResponseDto>(
      `${this.apiUrl}/register`,
      req
    );
  }

  /** Calls /social-login endpoint. */
  socialLogin(
    req: SocialLoginRequestDto
  ): Observable<AuthenticationResponseDto> {
    return this.http.post<AuthenticationResponseDto>(
      `${this.apiUrl}/social-login`,
      req
    );
  }

  /** Calls /refresh-token endpoint. */
  refreshToken(): Observable<AuthenticationResponseDto> {
    return this.http.post<AuthenticationResponseDto>(
      `${this.apiUrl}/refresh-token`,
      {},
      { withCredentials: true }
    );
  }

  /** Calls /logout endpoint. */
  logout() {
    return this.http.post<void>(
      `${this.apiUrl}/logout`,
      {},
      { withCredentials: true }
    );
  }

  /** Calls /me endpoint to retrieve user info. */
  me() {
    return this.http.get<UserDto>(`${this.apiUrl}/me`);
  }

  /** Requests password reset email. */
  forgotPassword(email: string) {
    return this.http.post<void>(`${this.apiUrl}/forgot-password`, { email });
  }

  /** Verifies user email using token. */
  verifyEmail(token: string) {
    return this.http.post<void>(
      `${this.apiUrl}/verify-email?token=${encodeURIComponent(token)}`,
      {}
    );
  }

  /** Resends verification email. */
  resendVerification(email: string) {
    return this.http.post<void>(`${this.apiUrl}/resend-verification`, {
      email
    });
  }

  /** Reset password */
  resetPassword(payload: ResetPasswordRequestDto) {
    return this.http.post<void>(`${this.apiUrl}/reset-password`, payload);
  }

  /** Revoke token  */
  revokeToken(refreshToken: string) {
    return this.http.post<void>(`${this.apiUrl}/revoke-token`, {
      refreshToken
    });
  }

  registerFacilityAdmin(
    req: FacilityAdminRegisterRequestDto
  ): Observable<FacilityAdminAuthenticationResponseDto> {
    return this.http.post<FacilityAdminAuthenticationResponseDto>(
      `${this.apiUrl}/register/facility-admin`,
      req
    );
  }
}
